import { useMemo } from 'react';
import { captureException } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { authorizedAxiosInstance } from '@/clients';
import type { JsonLogicRule } from '@/shared/components/filters/jsonLogic';
import type { SearchFilterJoin, SearchFilterOperator } from '@/shared/components/filters/searchFilters';

import type { SearchMatchMode, SearchMode } from '../../nuqs';

export * from './vendors';
export * from './searchMetadata';

export type FederatedSearchProvider =
    'calendar' | 'companies' | 'connections' | 'events' | 'executives' | 'filings' | 'fundraising' | 'peopleEnrichment';

export interface FederatedSearchQueryResult {
    data?: unknown;
    error?: Error | null;
    isFetching: boolean;
    provider: FederatedSearchProvider;
}

interface UseFederatedSearchQueriesInput {
    companyAdvancedFilterJsonLogic?: JsonLogicRule;
    companyFilters: Record<string, unknown>;
    companyFilterOperators?: Record<string, SearchFilterOperator>;
    connectionDomains: readonly ('documents' | 'people')[];
    enabledProviders: readonly FederatedSearchProvider[];
    filterEnabledProviders?: readonly FederatedSearchProvider[];
    filterJoin?: SearchFilterJoin;
    matchMode: SearchMatchMode;
    peopleFilters: Record<string, unknown>;
    query: string;
    searchMode: SearchMode;
}

const requestSearchProvider = async (
    provider: FederatedSearchProvider,
    query: string,
    matchMode: SearchMatchMode,
    searchMode: SearchMode,
    companyFilters: Record<string, unknown>,
    companyFilterOperators: Record<string, SearchFilterOperator>,
    companyAdvancedFilterJsonLogic: JsonLogicRule,
    peopleFilters: Record<string, unknown>,
    connectionDomains: readonly ('documents' | 'people')[],
    companySymbols: readonly string[],
    filterJoin: SearchFilterJoin,
    signal: AbortSignal,
): Promise<unknown> => {
    try {
        if (provider === 'companies') {
            const response = await authorizedAxiosInstance.post(
                '/api/search/vendors/fmp/companies',
                {
                    advancedFilterJsonLogic: companyAdvancedFilterJsonLogic,
                    filterJoin,
                    filterOperators: companyFilterOperators,
                    filters: companyFilters,
                    fragment: query,
                    includeConnectedCompanies: true,
                    matchMode,
                    searchMode,
                },
                { signal, timeout: 30_000 },
            );

            return response.data;
        }

        if (provider === 'connections') {
            const response = await authorizedAxiosInstance.post(
                '/api/command-palette',
                { domains: connectionDomains, matchMode, query },
                { signal, timeout: 30_000 },
            );

            return response.data;
        }

        if (provider === 'events') {
            const response = await authorizedAxiosInstance.post(
                '/api/search/vendors/fmp/events',
                { limit: 50 },
                { signal, timeout: 30_000 },
            );

            return response.data;
        }

        if (provider === 'executives') {
            const response = await authorizedAxiosInstance.post(
                '/api/search/vendors/fmp/executives',
                { matchMode, query },
                { signal, timeout: 30_000 },
            );

            return response.data;
        }

        if (provider === 'calendar') {
            const now = new Date();
            const from = new Date(now.getTime() - 30 * 86_400_000).toISOString().slice(0, 10);
            const to = new Date(now.getTime() + 180 * 86_400_000).toISOString().slice(0, 10);
            const [dividend, economic] = await Promise.all([
                authorizedAxiosInstance.get('/api/vendors/fmp/calendars/dividends', {
                    params: { from, to },
                    signal,
                    timeout: 30_000,
                }),
                authorizedAxiosInstance.get('/api/vendors/fmp/calendars/economic', {
                    params: { from, to },
                    signal,
                    timeout: 30_000,
                }),
            ]);

            return { dividend: dividend.data, economic: economic.data };
        }

        if (provider === 'filings') {
            const filings = await Promise.allSettled(
                companySymbols.slice(0, 5).map(async (symbol) => {
                    const response = await authorizedAxiosInstance.get(`/api/vendors/fmp/sec-filings/${symbol}`, {
                        params: { limit: 8 },
                        signal,
                        timeout: 30_000,
                    });

                    return Array.isArray(response.data)
                        ? response.data.map((filing: unknown) => ({ filing, symbol }))
                        : [];
                }),
            );

            filings.forEach((filingResult) => {
                if (filingResult.status === 'rejected' && !signal.aborted) {
                    captureException(filingResult.reason, { tags: { searchProvider: 'filings' } });
                }
            });

            return filings.flatMap((filingResult) => (filingResult.status === 'fulfilled' ? filingResult.value : []));
        }

        if (provider === 'peopleEnrichment') {
            const response = await authorizedAxiosInstance.post(
                '/api/vendors/people-data-labs/people/search',
                {
                    filters: {
                        educationLevels: peopleFilters.educationLevels,
                        email: peopleFilters.email,
                        firstName: peopleFilters.firstName,
                        jobTitles: peopleFilters.jobTitles,
                        keywords: peopleFilters.keywords,
                        lastName: peopleFilters.lastName,
                        roles: peopleFilters.roles,
                    },
                    matchMode,
                    query,
                    size: 12,
                },
                { signal, timeout: 30_000 },
            );

            return response.data;
        }

        const response = await authorizedAxiosInstance.get('/api/vendors/fmp/fundraising/search', {
            params: { name: query || undefined },
            signal,
            timeout: 30_000,
        });

        return response.data;
    } catch (error) {
        if (!signal.aborted) {
            captureException(error, { tags: { searchProvider: provider } });
        }
        throw error;
    }
};

const useSearchProviderQuery = (
    provider: FederatedSearchProvider,
    query: string,
    matchMode: SearchMatchMode,
    searchMode: SearchMode,
    companyFilters: Record<string, unknown>,
    companyFilterOperators: Record<string, SearchFilterOperator>,
    companyAdvancedFilterJsonLogic: JsonLogicRule,
    peopleFilters: Record<string, unknown>,
    connectionDomains: readonly ('documents' | 'people')[],
    companySymbols: readonly string[],
    filterJoin: SearchFilterJoin,
    isEnabled: boolean,
) =>
    useQuery({
        enabled: isEnabled,
        queryFn: ({ signal }) =>
            requestSearchProvider(
                provider,
                query,
                matchMode,
                searchMode,
                companyFilters,
                companyFilterOperators,
                companyAdvancedFilterJsonLogic,
                peopleFilters,
                connectionDomains,
                companySymbols,
                filterJoin,
                signal,
            ),
        queryKey: [
            'federated-search',
            provider,
            query,
            matchMode,
            searchMode,
            companyFilters,
            companyFilterOperators,
            companyAdvancedFilterJsonLogic,
            peopleFilters,
            connectionDomains,
            companySymbols,
            filterJoin,
        ],
        retry: 1,
        staleTime: 30_000,
    });

/** Executes each provider independently so one unavailable integration cannot invalidate all Search results. */
export const useFederatedSearchQueries = ({
    companyAdvancedFilterJsonLogic = {},
    companyFilters,
    companyFilterOperators = {},
    connectionDomains,
    enabledProviders,
    filterEnabledProviders = [],
    filterJoin = 'any',
    matchMode,
    peopleFilters,
    query,
    searchMode,
}: UseFederatedSearchQueriesInput): FederatedSearchQueryResult[] => {
    const normalizedQuery = query.trim();
    const enabled = (provider: FederatedSearchProvider) =>
        enabledProviders.includes(provider) &&
        (normalizedQuery.length > 0 || filterEnabledProviders.includes(provider));
    const companies = useSearchProviderQuery(
        'companies',
        normalizedQuery,
        matchMode,
        searchMode,
        companyFilters,
        companyFilterOperators,
        companyAdvancedFilterJsonLogic,
        peopleFilters,
        connectionDomains,
        [],
        filterJoin,
        enabled('companies'),
    );
    const calendar = useSearchProviderQuery(
        'calendar',
        normalizedQuery,
        matchMode,
        searchMode,
        companyFilters,
        companyFilterOperators,
        companyAdvancedFilterJsonLogic,
        peopleFilters,
        connectionDomains,
        [],
        filterJoin,
        enabled('calendar'),
    );
    const companySymbols = Array.isArray(companies.data)
        ? companies.data
              .map((company) => {
                  if (typeof company !== 'object' || company === null || Array.isArray(company)) {
                      return undefined;
                  }
                  const enrichment = company.enrichment;
                  const enrichmentSymbol =
                      typeof enrichment === 'object' && enrichment !== null && !Array.isArray(enrichment)
                          ? enrichment.symbol
                          : undefined;

                  return typeof enrichmentSymbol === 'string'
                      ? enrichmentSymbol
                      : typeof company.symbol === 'string'
                        ? company.symbol
                        : undefined;
              })
              .filter((symbol): symbol is string => Boolean(symbol))
              .slice(0, 5)
        : [];
    const connections = useSearchProviderQuery(
        'connections',
        normalizedQuery,
        matchMode,
        searchMode,
        companyFilters,
        companyFilterOperators,
        companyAdvancedFilterJsonLogic,
        peopleFilters,
        connectionDomains,
        [],
        filterJoin,
        enabled('connections'),
    );
    const events = useSearchProviderQuery(
        'events',
        normalizedQuery,
        matchMode,
        searchMode,
        companyFilters,
        companyFilterOperators,
        companyAdvancedFilterJsonLogic,
        peopleFilters,
        connectionDomains,
        [],
        filterJoin,
        enabled('events'),
    );
    const executives = useSearchProviderQuery(
        'executives',
        normalizedQuery,
        matchMode,
        searchMode,
        companyFilters,
        companyFilterOperators,
        companyAdvancedFilterJsonLogic,
        peopleFilters,
        connectionDomains,
        [],
        filterJoin,
        enabled('executives'),
    );
    const filings = useSearchProviderQuery(
        'filings',
        normalizedQuery,
        matchMode,
        searchMode,
        companyFilters,
        companyFilterOperators,
        companyAdvancedFilterJsonLogic,
        peopleFilters,
        connectionDomains,
        companySymbols,
        filterJoin,
        enabled('filings') && companySymbols.length > 0,
    );
    const fundraising = useSearchProviderQuery(
        'fundraising',
        normalizedQuery,
        matchMode,
        searchMode,
        companyFilters,
        companyFilterOperators,
        companyAdvancedFilterJsonLogic,
        peopleFilters,
        connectionDomains,
        [],
        filterJoin,
        enabled('fundraising'),
    );
    const peopleEnrichment = useSearchProviderQuery(
        'peopleEnrichment',
        normalizedQuery,
        matchMode,
        searchMode,
        companyFilters,
        companyFilterOperators,
        companyAdvancedFilterJsonLogic,
        peopleFilters,
        connectionDomains,
        [],
        filterJoin,
        enabled('peopleEnrichment'),
    );

    return useMemo(
        () => [
            { data: calendar.data, error: calendar.error, isFetching: calendar.isFetching, provider: 'calendar' },
            { data: companies.data, error: companies.error, isFetching: companies.isFetching, provider: 'companies' },
            {
                data: connections.data,
                error: connections.error,
                isFetching: connections.isFetching,
                provider: 'connections',
            },
            { data: events.data, error: events.error, isFetching: events.isFetching, provider: 'events' },
            {
                data: executives.data,
                error: executives.error,
                isFetching: executives.isFetching,
                provider: 'executives',
            },
            { data: filings.data, error: filings.error, isFetching: filings.isFetching, provider: 'filings' },
            {
                data: fundraising.data,
                error: fundraising.error,
                isFetching: fundraising.isFetching,
                provider: 'fundraising',
            },
            {
                data: peopleEnrichment.data,
                error: peopleEnrichment.error,
                isFetching: peopleEnrichment.isFetching,
                provider: 'peopleEnrichment',
            },
        ],
        [
            calendar.data,
            calendar.error,
            calendar.isFetching,
            companies.data,
            companies.error,
            companies.isFetching,
            connections.data,
            connections.error,
            connections.isFetching,
            events.data,
            events.error,
            events.isFetching,
            executives.data,
            executives.error,
            executives.isFetching,
            filings.data,
            filings.error,
            filings.isFetching,
            fundraising.data,
            fundraising.error,
            fundraising.isFetching,
            peopleEnrichment.data,
            peopleEnrichment.error,
            peopleEnrichment.isFetching,
        ],
    );
};
